import { Quote, PremiumAdjustmentLimit } from '@client/model/quotesModel';
import { FundingPolicyType } from '@shared';
import { toKnownInvestmentBound } from '@client/utils/investmentBounds';
import { getMultiplierForPremium } from './lincolnPricingUtils';
import { getOAMultipliersForPremium, isOneAmericaAssetCare } from './oneAmericaPricingUtils';

const SECURIAN_MIN_DEATH_BENEFIT_CAP = 10000;
const SECURIAN_MIN_DEATH_BENEFIT_RATE = 0.1;

function calculateMinGuaranteedDeathBenefit(
  fundingPolicyType: number,
  scaledDeathBenefit: number,
  originalValue: number | undefined,
  scalingFactor: number,
): number | undefined {
  if (fundingPolicyType === FundingPolicyType.secureCareThree || fundingPolicyType === FundingPolicyType.secureCareFour) {
    return Math.min(
      SECURIAN_MIN_DEATH_BENEFIT_CAP,
      scaledDeathBenefit * SECURIAN_MIN_DEATH_BENEFIT_RATE,
    );
  }
  if (fundingPolicyType === FundingPolicyType.nylAssetFlex) {
    // according to the NYL asset flex product details
    // the minimum guaranteed death benefit is the lesser of 25k or 10% of the death benefit
    return Math.min(25000, scaledDeathBenefit * 0.1);
  }
  return originalValue ? originalValue * scalingFactor : undefined;
}

/**
 * `outOfRange` means the carrier's band table could not price the requested premium, so the
 * factors carry no information and callers must not scale benefits by them.
 */
export type QuoteScalingFactors = { db: number; ltc: number; outOfRange?: boolean };

const UNPRICEABLE_FACTORS: QuoteScalingFactors = { db: 1, ltc: 1, outOfRange: true };

export function getScalingFactorsForQuote(
  quote: Quote,
  newPremium: number,
): QuoteScalingFactors {
  const linearFactor = newPremium / quote.premiumAmount;

  if (quote.fundingPolicyType === FundingPolicyType.moneyGuardFixedAdvantage) {
    const originalMultiplier = getMultiplierForPremium(quote.premiumAmount);
    const newMultiplier = getMultiplierForPremium(newPremium);
    // originalMultiplier is a divisor, so 0 is rejected alongside undefined.
    if (newMultiplier === undefined || !originalMultiplier) return UNPRICEABLE_FACTORS;
    const factor = linearFactor * (newMultiplier / originalMultiplier);
    return { db: factor, ltc: factor };
  }

  if (isOneAmericaAssetCare(quote.fundingPolicyType)) {
    const original = getOAMultipliersForPremium(
      quote.fundingPolicyType,
      quote.productFundingOption,
      quote.aobInflation,
      quote.cobInflation,
      quote.cobDuration,
      quote.premiumAmount,
      quote.usaStateCategory,
      quote.primaryAge,
      quote.primaryGender,
      quote.aobDuration,
      quote.secondaryAge,
    );
    const target = getOAMultipliersForPremium(
      quote.fundingPolicyType,
      quote.productFundingOption,
      quote.aobInflation,
      quote.cobInflation,
      quote.cobDuration,
      newPremium,
      quote.usaStateCategory,
      quote.primaryAge,
      quote.primaryGender,
      quote.aobDuration,
      quote.secondaryAge,
    );
    // Only 'above-band' is unpriceable; a 'no-band' miss yields inert 1.0s and so scales
    // linearly, which is this row's established behavior (see OABandMiss).
    if (original.miss === 'above-band' || target.miss === 'above-band') {
      return UNPRICEABLE_FACTORS;
    }
    if (!original.db || !original.ltc) return UNPRICEABLE_FACTORS;
    return {
      db: linearFactor * (target.db / original.db),
      ltc: linearFactor * (target.ltc / original.ltc),
    };
  }

  return { db: linearFactor, ltc: linearFactor };
}

function scaleValue(value: number | undefined, scalingFactor: number): number | undefined {
  return value !== undefined ? value * scalingFactor : undefined;
}

/**
 * True when the quote is shown at exactly the premium that was requested — the product
 * honored it with no clamp and no pin. Only these quotes are directly comparable at that
 * premium; the rest are displayed at some other premium, so their ROI answers a different
 * question.
 */
export const isPremiumAsRequested = (quote: Pick<Quote, 'premiumClamped'>): boolean =>
  !quote.premiumClamped;

/**
 * Adjusts a quote's parameters based on a new premium amount.
 * MoneyGuard Fixed Advantage 2025 and OneAmerica AssetCare use premium band
 * correction for non-linear pricing. OneAmerica uses separate correction
 * factors for death benefit vs total LTC benefit.
 * MoneyGuard also applies a floor guarantee ensuring death benefit never
 * falls below premium. All other products use simple linear scaling.
 */
export const adjustQuoteForPremium = (originalQuote: Quote, newPremiumAmount: number): Quote => {
  if (!originalQuote.premiumAmount || originalQuote.premiumAmount <= 0) {
    return originalQuote;
  }

  // A quote can only be issued at a premium within its own [investmentMin, investmentMax]
  // range. Clamp to that range so the displayed cost/coverage/ROI (and the downstream
  // illustration PDF and saved policy) reflect a premium the product can actually honor.
  //
  // A non-positive bound means "unknown" (see toKnownInvestmentBound), not "unbounded".
  // Treating it as no-clamp let linear carriers scale benefits to any premium while holding
  // ROI constant, so an implausible premium looked as good as a realistic one. With bounds
  // unknown the only premium known to be issuable is the quote's own, so pin there.
  // Only the bound in the direction of travel is binding, so a row missing just one bound
  // keeps its adjustability in the other direction.
  const minBound = toKnownInvestmentBound(originalQuote.investmentMin);
  const maxBound = toKnownInvestmentBound(originalQuote.investmentMax);

  let clampedPremium = newPremiumAmount;
  let premiumClamped: PremiumAdjustmentLimit | undefined;

  if (newPremiumAmount > originalQuote.premiumAmount) {
    if (maxBound === undefined) {
      clampedPremium = originalQuote.premiumAmount;
      premiumClamped = 'max-unknown';
    } else if (newPremiumAmount > maxBound) {
      clampedPremium = maxBound;
      premiumClamped = 'max';
    }
  } else if (newPremiumAmount < originalQuote.premiumAmount) {
    if (minBound === undefined) {
      clampedPremium = originalQuote.premiumAmount;
      premiumClamped = 'min-unknown';
    } else if (newPremiumAmount < minBound) {
      clampedPremium = minBound;
      premiumClamped = 'min';
    }
  }

  const { db: dbFactor, ltc: ltcFactor, outOfRange } = getScalingFactorsForQuote(
    originalQuote,
    clampedPremium,
  );

  // Lincoln/OneAmerica price from published premium bands, and those bands can be narrower
  // than the stored investmentMin/Max. Past the last band there is no published multiplier,
  // so the factors come back as 1 (UNPRICEABLE_FACTORS) and the benefits stay unscaled —
  // show the quote at its own premium rather than at one it was never priced for.
  if (outOfRange) {
    clampedPremium = originalQuote.premiumAmount;
    premiumClamped = 'not-priceable';
  }

  // Pinning can land exactly on the requested premium, in which case nothing was limited.
  if (clampedPremium === newPremiumAmount) {
    premiumClamped = undefined;
  }

  let scaledDeathBenefit = originalQuote.deathBenefitInitial * dbFactor;

  if (originalQuote.fundingPolicyType === FundingPolicyType.moneyGuardFixedAdvantage) {
    scaledDeathBenefit = Math.max(scaledDeathBenefit, clampedPremium);
  }

  const adjustedQuote: Quote = {
    ...originalQuote,
    premiumAmount: clampedPremium,
    premiumClamped,

    deathBenefitInitial: scaledDeathBenefit,
    deathBenefitMinimumGuaranteedIndividual: calculateMinGuaranteedDeathBenefit(
      originalQuote.fundingPolicyType,
      scaledDeathBenefit,
      originalQuote.deathBenefitMinimumGuaranteedIndividual,
      dbFactor,
    ),
    deathBenefitMinimumGuaranteedJoint: scaleValue(
      originalQuote.deathBenefitMinimumGuaranteedJoint,
      dbFactor,
    ),

    ltcBenefitMonthlyIndividual: originalQuote.ltcBenefitMonthlyIndividual * dbFactor,
    ltcBenefitAnnualIndividual: scaleValue(originalQuote.ltcBenefitAnnualIndividual, dbFactor),
    ltcBenefitTotal: originalQuote.ltcBenefitTotal * ltcFactor,
    maxMonthlyLTCBenefitReimbursement: scaleValue(
      originalQuote.maxMonthlyLTCBenefitReimbursement,
      dbFactor,
    ),
    maxMonthlyLTCBenefitIndemnity: scaleValue(
      originalQuote.maxMonthlyLTCBenefitIndemnity,
      dbFactor,
    ),

    ltcBenefitDurationMonths: originalQuote.ltcBenefitDurationMonths,
    investmentMin: originalQuote.investmentMin,
    investmentMax: originalQuote.investmentMax,

    projectedCostCoverage: undefined,
    roi: undefined,
    roiValues: undefined,
    formattedROI: undefined,
  };

  return adjustedQuote;
};
export const adjustQuotesForPremium = (
  originalQuotes: Quote[],
  newPremiumAmount: number,
): Quote[] => originalQuotes.map(quote => adjustQuoteForPremium(quote, newPremiumAmount));

/**
 * Adjusts an EquiTrust quote's LTC benefits based on a new coverage ratio
 * LTC benefits scale proportionally with coverage ratio changes
 * Premium and death benefit remain unchanged
 */
export const adjustQuoteForCoverageRatio = (
  originalQuote: Quote,
  newCoverageRatio: number,
): Quote => {
  const originalCoverageRatio = originalQuote.coverageRatio;
  if (!originalCoverageRatio || originalCoverageRatio <= 0) {
    return originalQuote;
  }

  const minRatio = originalQuote.minCoverageRatio || originalCoverageRatio;
  const maxRatio = originalQuote.maxCoverageRatio || originalCoverageRatio;
  const clampedRatio = Math.max(minRatio, Math.min(maxRatio, newCoverageRatio));

  const scalingFactor = clampedRatio / originalCoverageRatio;

  return {
    ...originalQuote,
    coverageRatio: clampedRatio,
    ltcBenefitMonthlyIndividual: originalQuote.ltcBenefitMonthlyIndividual * scalingFactor,
    ltcBenefitAnnualIndividual: scaleValue(originalQuote.ltcBenefitAnnualIndividual, scalingFactor),
    ltcBenefitTotal: originalQuote.ltcBenefitTotal * scalingFactor,
    projectedCostCoverage: undefined,
    roi: undefined,
    roiValues: undefined,
    formattedROI: undefined,
  };
};
